import { evictReturnedListing } from "@/lib/supplierCorrectionVisibility";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { Socket } from "socket.io-client";
import { useQueryClient } from "@/api/zustandQuery";
import { EVENT_INVALIDATION_MAP,refinementQueueInvalidations } from "@/lib/socketInvalidations";
import { env } from "@/config/env";
import { useAuth } from "@/api/auth";
import {
  FINAL_LISTING_INVALIDATION_EVENTS,
  invalidateFinalListingQueries,
} from "@/lib/finalListingQueryInvalidation";

interface SocketContextValue {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextValue>({ socket: null });

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, principal } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [activeSocket, setActiveSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!accessToken || !principal) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setActiveSocket(null);
      }
      return;
    }

    let cancelled = false;
    let connectedSocket: Socket | null = null;
    void import("socket.io-client").then(({ io }) => {
      if (cancelled) return;
      const socket = io(`${env.apiBaseUrl}/events`, {
        transports: ["websocket", "polling"],
        auth: { token: accessToken },
        withCredentials: true,
        reconnection: true,
        reconnectionDelay: 2000,
      });

      connectedSocket = socket;

      socketRef.current = socket;
      setActiveSocket(socket);

      socket.on("connect", () => {
        if (env.debug) console.log("[socket] connected", socket.id);
        // Completion events can be missed during transport interruptions.
        // Re-read only active, authoritative queue reads without resubmitting
        // provider work. Listing Intake needs the same catch-up because a
        // supplier can submit while this browser is reconnecting.
        void queryClient.invalidateQueries({ queryKey: ["page", "listing-intake"] });
        // Final Gate status can change while this browser is reconnecting,
        // for example when the last approved slideshow makes a listing ready
        // to publish. Re-read the active Gate rows immediately so an operator
        // never needs to reload the browser to see that transition.
        invalidateFinalListingQueries(queryClient, "listing.release.updated");
        void queryClient.invalidateQueries({ queryKey: ["page", "image-builder"] });
        void queryClient.invalidateQueries({ queryKey: ["assets", "listing-images"] });
      });

      socket.on("disconnect", (reason) => {
        if (env.debug) console.log("[socket] disconnected", reason);
      });

      socket.on("connect_error", (error) => {
        if (env.debug) console.error("[socket] connect error", error.message);
      });

      const events = new Set([
        ...Object.keys(EVENT_INVALIDATION_MAP),
        ...FINAL_LISTING_INVALIDATION_EVENTS,
      ]);
      events.forEach((event) => {
        socket.on(event, (payload: unknown) => {
          if (env.debug) console.log(`[socket] ${event}`, payload);
          evictReturnedListing(queryClient, payload);
          invalidateFinalListingQueries(queryClient, event);
          const finalQueriesHandled = (FINAL_LISTING_INVALIDATION_EVENTS as readonly string[]).includes(event);
          const invalidated = new Set<string>();
          const keys = [...(EVENT_INVALIDATION_MAP[event] ?? []),...refinementQueueInvalidations(event,payload)];
          for (let i = 0; i < keys.length; i += 2) {
            const queryKey = keys[i + 1] ? [keys[i], keys[i + 1]] : [keys[i]];
            if (finalQueriesHandled && ['final-approval-queue', 'final-approval-detail'].includes(queryKey[0])) continue;
            const hash = JSON.stringify(queryKey);
            if (invalidated.has(hash)) continue;
            invalidated.add(hash);
            queryClient.invalidateQueries({ queryKey });
          }
        });
      });
    }).catch(() => {
      // HTTP reads/polling remain available if the optional transport cannot load.
      if (env.debug) console.error("[socket] transport unavailable");
    });

    return () => {
      cancelled = true;
      connectedSocket?.disconnect();
      socketRef.current = null;
      setActiveSocket(null);
    };
  }, [accessToken, principal, queryClient]);

  return (
    <SocketContext.Provider value={{ socket: activeSocket }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket(): SocketContextValue {
  return useContext(SocketContext);
}
